/*
 * Feet as the catalog stores them: in hundredths, so 0.1 and 0.10 are one
 * size and a step of 0.1 lands on the same sizes every time. Shared by the
 * pieces whose shape is a measure rather than a drawing — dance floors and
 * risers.
 */

/** Feet are stored and compared in hundredths, so 0.1 and 0.10 are one size. */
const HUNDREDTHS_PER_FOOT = 100

/** Nearest hundredth of a foot. The unit every comparison of these measures uses. */
export function feetToHundredths(feet: number): number {
  return Math.round(feet * HUNDREDTHS_PER_FOOT)
}

/** The foot value a stored hundredths count stands for. */
export function hundredthsToFeet(hundredths: number): number {
  return hundredths / HUNDREDTHS_PER_FOOT
}

/** Null stays null. A stored count becomes the feet the wire and the forms speak. */
export function hundredthsToFeetOrNull(hundredths: number | null): number | null {
  return hundredths === null ? null : hundredthsToFeet(hundredths)
}

/**
 * True when `feet` is a whole number of hundredths.
 *
 * `0.1 * 100` is not 10 in floating point, so the test is against the rounded
 * hundredth rather than a decimal-string check. A third decimal place misses
 * that hundredth by a visible amount and fails.
 */
export function isHundredthsFeet(feet: number): boolean {
  if (!Number.isFinite(feet)) return false
  return Math.abs(feet * HUNDREDTHS_PER_FOOT - Math.round(feet * HUNDREDTHS_PER_FOOT)) < 1e-6
}

/** What a size field holds. Blank is "not filled in", not zero. */
export function parseFeetInput(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const feet = Number(trimmed)
  return Number.isFinite(feet) ? feet : null
}

/** "10", "10.5", "10.25": a foot measure as the forms and labels write it. */
export function formatFeet(feet: number): string {
  const hundredths = Math.abs(feetToHundredths(feet))
  const whole = Math.trunc(hundredths / HUNDREDTHS_PER_FOOT)
  const frac = hundredths % HUNDREDTHS_PER_FOOT
  if (frac === 0) return String(whole)
  if (frac % 10 === 0) return `${String(whole)}.${String(frac / 10)}`
  return `${String(whole)}.${String(frac).padStart(2, '0')}`
}
